import React, { useRef, useCallback } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ReportStore from '../DataRepository/Reports';
import { reportsModel } from '../Axios/AxiosConfig';
import {
    IconButton, CircularProgress, TextField, Button, Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, Tabs, Tab, TableSortLabel
} from '@mui/material';

import Accordion from '@mui/material/Accordion';
import { CheckCircle, ArrowUpward, Edit, Delete as DeleteIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from '@mui/icons-material/Add';
import AccordionActions from '@mui/material/AccordionActions';
import { Circle, AlertTriangle } from 'lucide-react';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ChevronLeft from '@mui/icons-material/ChevronLeft';

function ViewReportList() {
    const { Reports, isReportLoading, getReports } = ReportStore()

    const { UID, id } = useParams()

    const navigate = useNavigate()

    useEffect(() => {
        getReports(UID)
    }, [])

    const [editMode, setEditMode] = useState(false);
    const [editableReports, setEditableReports] = useState([]);
    const [activeFileTab, setActiveFileTab] = useState(0);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [extractionStatus, setExtractionStatus] = useState('unknown');
    const [timeLeft, setTimeLeft] = useState(120);
    const [analysisDisplayTab, setAnalysisDisplayTab] = useState(0); // 0: Table, 1: Details (Accordion)
    const [isReanalyzing, setIsReanalyzing] = useState(false);
    const pollingRef = useRef(null);

    const toggleEdit = () => setEditMode(prev => !prev);

    const selectedReport = Reports.filter((e) => e.id === id)?.[0]

    useEffect(() => {
        if (selectedReport?.Reports?.length) {
            const parsedReports = selectedReport.Reports.map((report) => {
                try {
                    return JSON.parse(report?.ai_analysis || '{}');
                } catch {
                    return {};
                }
            });
            setEditableReports(parsedReports);
        }
    }, [selectedReport]);

    // Sync extractionStatus when selectedReport loads/changes
    useEffect(() => {
        if (selectedReport?.extractionStatus) {
            setExtractionStatus(selectedReport.extractionStatus);
        }
    }, [selectedReport]);

    // Poll /getReportStatus every 5s while extraction is pending
    useEffect(() => {
        if (extractionStatus !== 'pending') return;

        pollingRef.current = setInterval(async () => {
            try {
                const res = await reportsModel.post('/getReportStatus', { id });
                const newStatus = res?.data?.extractionStatus;
                if (newStatus && newStatus !== 'pending') {
                    setExtractionStatus(newStatus);
                    clearInterval(pollingRef.current);
                    getReports(UID); // Refresh to get updated AI data
                }
            } catch (e) {
                console.error('Polling error:', e);
            }
        }, 5000);

        return () => clearInterval(pollingRef.current);
    }, [extractionStatus]);

    // Countdown timer for pending status
    useEffect(() => {
        let timer;
        if (extractionStatus === 'pending') {
            setTimeLeft(120); // Reset to 2 minutes
            timer = setInterval(() => {
                setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [extractionStatus]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };


    const handleParamChange = (reportIndex, type, paramIndex, field, value) => {
        const updated = [...editableReports];
        const currentReport = { ...updated[reportIndex] };
        const targetList = [...(currentReport[type] || [])];
        const updatedParam = { ...targetList[paramIndex], [field]: value };

        if (field === 'status') {
            const isNormalStatus = value.toLowerCase().includes('normal');
            const currentlyInNormal = type === 'normal_parameters';

            // Move from abnormal → normal
            if (isNormalStatus && !currentlyInNormal) {
                const abnormalList = [...(currentReport.abnormal_parameters || [])];
                // remove param safely by id (or reference) instead of index
                const filteredAbnormal = abnormalList.filter((_, i) => i !== paramIndex);

                currentReport.abnormal_parameters = filteredAbnormal;
                currentReport.normal_parameters = [
                    ...(currentReport.normal_parameters || []),
                    updatedParam
                ];
            }

            // Move from normal → abnormal
            else if (!isNormalStatus && currentlyInNormal) {
                const normalList = [...(currentReport.normal_parameters || [])];
                const filteredNormal = normalList.filter((_, i) => i !== paramIndex);

                currentReport.normal_parameters = filteredNormal;
                currentReport.abnormal_parameters = [
                    ...(currentReport.abnormal_parameters || []),
                    updatedParam
                ];
            } else {
                // Just update in place if no movement between lists
                targetList[paramIndex] = updatedParam;
                currentReport[type] = targetList;
            }

            updated[reportIndex] = currentReport;
            setEditableReports(updated);
            return;
        }

        targetList[paramIndex] = updatedParam;
        currentReport[type] = targetList;
        updated[reportIndex] = currentReport;
        setEditableReports(updated);
    };

    const handleAddParam = () => {
        setEditMode(true);
        const updated = [...editableReports];
        if (updated.length > activeFileTab) {
            const currentReport = updated[activeFileTab] || {};
            updated[activeFileTab] = {
                ...currentReport,
                normal_parameters: [
                    { name: "", value: "", status: "normal", reference_range: "", reason: "", isAddedManually: true },
                    ...(currentReport.normal_parameters || [])
                ]
            };
            setEditableReports(updated);
        }
    };

    const handleRemoveParam = (reportIndex, type, paramIndex) => {
        const updated = [...editableReports];
        const currentReport = { ...updated[reportIndex] };
        const targetList = [...(currentReport[type] || [])];

        targetList.splice(paramIndex, 1);
        currentReport[type] = targetList;
        updated[reportIndex] = currentReport;

        setEditableReports(updated);
    };

    const handleReanalyze = async () => {
        if (isReanalyzing || extractionStatus === 'pending') return;
        try {
            setIsReanalyzing(true);
            await reportsModel.post(`/reanalyze/${id}`);
            setExtractionStatus('pending'); // triggers polling useEffect automatically
        } catch (error) {
            console.error('Re-analysis failed to start:', error);
        } finally {
            setIsReanalyzing(false);
        }
    };

    const handleSaveAll = async () => {
        try {
            const updated = selectedReport.Reports.map((r, i) => ({
                ...r,
                ai_analysis: JSON.stringify(editableReports[i] || {})
            }));

            const res = await reportsModel.post('/editAnalyzedReport', {
                id,
                editableReports: updated
            });

            if (res.status === 200) setEditMode(false);
        } catch (error) {
            console.error("Failed to save all reports", error);
        }
    };

    const getStatusTag = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Low</span>;
            case 'high':
                return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 border border-red-300">High</span>;
            case 'normal':
                return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 border border-green-300">Normal</span>;
            case 'borderline':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800 border border-orange-300">Borderline</span>;
            case 'borderline_low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-900 border border-yellow-400">Borderline Low</span>;
            case 'borderline_high':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-200 text-orange-900 border border-orange-400">Borderline High</span>;
            case 'critical':
                return <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 border border-purple-300">Critical</span>;
            case 'missed':
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600 border border-gray-300">Missed</span>;
            default:
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200">Unknown</span>;
        }
    };

    const getIcon = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
            case 'high':
                return <AlertTriangle className="text-red-500 w-5 h-5 mr-2" />;
            case 'normal':
                return <CheckCircle className="text-green-500 w-5 h-5 mr-2" />;
        }
    };



    return (
        <div className="All-products-section-main">
            <nav className="flex flex-row p-2 items-center justify-between">
                <div className="flex flex-row items-center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ChevronLeft />
                    </IconButton>
                    <p className="mb-0">Reports</p>
                </div>
            </nav>

            <div className="Reports-main-page px-4 py-2">
                <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif", mb: 2 }}>
                    {selectedReport?.ReportType}
                </Typography>

                <div className="flex gap-6">

                    <div style={{ flex: 2, display: "flex", flexDirection: "column", height: "80vh" }}>
                        <Typography variant="subtitle1" sx={{ fontFamily: "'Poppins', sans-serif", mb: 2, fontWeight: 600 }}>
                            File Preview
                        </Typography>

                        {selectedReport?.Reports?.length > 1 && (
                            <Tabs
                                value={activeFileTab}
                                onChange={(e, newValue) => { setActiveFileTab(newValue); setPreviewLoading(true); }}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    mb: 2,
                                    '& .MuiTabs-indicator': { backgroundColor: 'var(--themePrimaryColor, #DA6E0B)' },
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 500,
                                        minWidth: 'auto',
                                        px: 2,
                                        '&.Mui-selected': { color: 'var(--themePrimaryColor, #DA6E0B)' }
                                    }
                                }}
                            >
                                {selectedReport.Reports.map((e, index) => (
                                    <Tab key={index} label={`File ${index + 1}`} />
                                ))}
                            </Tabs>
                        )}

                        <Paper
                            elevation={1}
                            sx={{
                                flex: 1,
                                width: "100%",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "#f5f5f5",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                                position: "relative"
                            }}
                        >
                            {selectedReport?.Reports?.[activeFileTab] ? (
                                <>
                                    {previewLoading && (
                                        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(245,245,245,0.9)", zIndex: 2 }}>
                                            <CircularProgress sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                                        </Box>
                                    )}
                                    {selectedReport.filetype === "images" || selectedReport.Reports[activeFileTab].Location?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ? (
                                        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", overflow: "auto", padding: "8px" }}>
                                            <img
                                                src={selectedReport.Reports[activeFileTab].Location}
                                                alt={`Report ${activeFileTab + 1}`}
                                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                onLoad={() => setPreviewLoading(false)}
                                            />
                                        </div>
                                    ) : (
                                        <iframe
                                            src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedReport.Reports[activeFileTab].Location)}&embedded=true`}
                                            title={`Report ${activeFileTab + 1}`}
                                            style={{ width: "100%", height: "100%", border: "none" }}
                                            onLoad={() => setPreviewLoading(false)}
                                        />
                                    )}
                                    <Box sx={{ p: 1, backgroundColor: "#fff", borderTop: "1px solid #e0e0e0" }}>
                                        <Typography variant="caption" sx={{ fontFamily: "'Poppins', sans-serif", color: "#666" }}>
                                            {selectedReport.Reports[activeFileTab]?.Key?.split("/")[3]}
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                    <Typography variant="body2" color="textSecondary">No file preview available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </div>


                    <div style={{ flex: 3, overflowY: "auto", maxHeight: "80vh" }}>
                        <div className="flex justify-end mb-2 gap-2">
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "var(--themePrimaryColor, #DA6E0B)", textTransform: "none", fontFamily: "'Poppins', sans-serif", borderRadius: "5px" }}
                                onClick={toggleEdit}
                            >
                                Edit All
                            </Button>
                            {editMode && (
                                <Button
                                    variant="contained"
                                    style={{ backgroundColor: "#4CAF50", textTransform: "none", fontFamily: "'Poppins', sans-serif", borderRadius: "5px" }}
                                    onClick={handleSaveAll}
                                >
                                    Save All
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "var(--themePrimaryColor, #DA6E0B)", textTransform: "none", fontFamily: "'Poppins', sans-serif", borderRadius: "5px" }}
                                endIcon={<AddIcon />}
                                onClick={handleAddParam}
                            >
                                Add
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={isReanalyzing || extractionStatus === 'pending'}
                                startIcon={
                                    (isReanalyzing || extractionStatus === 'pending')
                                        ? <CircularProgress size={16} sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                                        : <RefreshIcon />
                                }
                                style={{
                                    borderColor: "var(--themePrimaryColor, #DA6E0B)",
                                    color: "var(--themePrimaryColor, #DA6E0B)",
                                    textTransform: "none",
                                    fontFamily: "'Poppins', sans-serif",
                                    borderRadius: "5px"
                                }}
                                onClick={handleReanalyze}
                            >
                                {(isReanalyzing || extractionStatus === 'pending') ? 'Processing...' : 'Re-analyze'}
                            </Button>
                        </div>
                        {(() => {
                            const i = activeFileTab;
                            const parsed = editableReports[i];

                            if (extractionStatus === 'pending') return (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "300px", gap: 2, p: 3, backgroundColor: '#FFF8F2', borderRadius: 2, border: '1px solid #f0e0cc' }}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress size={80} thickness={2} sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                                        <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography variant="caption" component="div" color="textSecondary" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                                {formatTime(timeLeft)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif", color: 'var(--themePrimaryColor, #312110)', fontWeight: 600 }}>
                                        Analyzing Medical Reports...
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "'Poppins', sans-serif", color: 'var(--themePrimaryColor, #312110)', textAlign: 'center', maxWidth: '300px' }}>
                                        AI extraction usually takes about 2 minutes. Please wait while we process your files.
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", mt: 1 }}>
                                        This page will refresh automatically when done.
                                    </Typography>
                                </Box>
                            );

                            if (extractionStatus === 'failed') return (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "200px", gap: 1, p: 3, backgroundColor: '#FFF5F5', borderRadius: 2, border: '1px solid #ffcccc' }}>
                                    <Typography variant="body2" sx={{ fontFamily: "'Poppins', sans-serif", color: '#c62828', fontWeight: 500 }}>
                                        ⚠️ AI extraction failed.
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#999', fontFamily: "'Poppins', sans-serif" }}>
                                        You can try re-analyzing from the report options.
                                    </Typography>
                                </Box>
                            );

                            if (!parsed || parsed === "AI analysis failed") return (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>No analysis data available for this file</Typography>
                                </Box>
                            );

                            const types = ["abnormal_parameters", "normal_parameters"];

                            return (
                                <div className="mb-6 p-3 bg-[color:var(--themeSurfaceColor,#FFFFFF)] shadow rounded">
                                    <div className="flex items-center justify-between mb-4">
                                        <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                            Report #{i + 1}
                                        </Typography>
                                        {!editMode && (
                                            <Tabs
                                                value={analysisDisplayTab}
                                                onChange={(e, newValue) => setAnalysisDisplayTab(newValue)}
                                                sx={{
                                                    minHeight: '36px',
                                                    '& .MuiTab-root': {
                                                        minHeight: '36px',
                                                        textTransform: 'none',
                                                        fontSize: '0.875rem',
                                                        fontFamily: "'Poppins', sans-serif",
                                                        px: 2
                                                    },
                                                    '& .MuiTabs-indicator': { backgroundColor: 'var(--themePrimaryColor, #DA6E0B)' },
                                                    '& .Mui-selected': { color: 'var(--themePrimaryColor, #DA6E0B) !important' }
                                                }}
                                            >
                                                <Tab label="Table View" />
                                                <Tab label="Details View" />
                                            </Tabs>
                                        )}
                                    </div>

                                    {editMode ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Name</strong></TableCell>
                                                    <TableCell><strong>Value</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell><strong>Reference Range</strong></TableCell>
                                                    <TableCell><strong>Note</strong></TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {types.flatMap((type) =>
                                                    (parsed?.[type] || []).map((param, j) => (
                                                        <TableRow key={`${i}-${type}-${j}`}>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.name || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "name", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.value || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "value", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <FormControl size="small" fullWidth>
                                                                    <Select
                                                                        value={param.status || ""}
                                                                        onChange={(e) => handleParamChange(i, type, j, "status", e.target.value)}
                                                                    >
                                                                        <MenuItem value="normal">Normal</MenuItem>
                                                                        <MenuItem value="low">Low</MenuItem>
                                                                        <MenuItem value="high">High</MenuItem>
                                                                        <MenuItem value="borderline">Borderline</MenuItem>
                                                                        <MenuItem value="borderline_low">Borderline Low</MenuItem>
                                                                        <MenuItem value="borderline_high">Borderline High</MenuItem>
                                                                        <MenuItem value="critical">Critical</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.reference_range || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "reference_range", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.reason || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "reason", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {param.isAddedManually && (
                                                                    <IconButton onClick={() => handleRemoveParam(i, type, j)} color="error">
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    ) : analysisDisplayTab === 0 ? (
                                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee' }}>
                                            <Table size="small">
                                                <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>Parameter</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>Value</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>Ref. Range</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>Note</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {types.flatMap((type) =>
                                                        (parsed?.[type] || []).map((param, j) => (
                                                            <TableRow key={`table-${i}-${type}-${j}`} hover>
                                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }}>{param.name}</TableCell>
                                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem', fontWeight: 500 }}>{param.value}</TableCell>
                                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem', color: '#666' }}>{param.reference_range}</TableCell>
                                                                <TableCell>{getStatusTag(param.status)}</TableCell>
                                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', color: '#555', maxWidth: '200px' }}>{param.reason}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        types.flatMap((type) =>
                                            (parsed?.[type] || []).map((param, j) => {
                                                const status = (param.status || "").toLowerCase();
                                                const backgroundColor =
                                                    status === "low" || status === "high"
                                                        ? "#FFF5F5"
                                                        : status === "normal"
                                                            ? "var(--themeSecondaryColor, #F1EFEC)"
                                                            : "#F5F5F5";

                                                return (
                                                    <Accordion key={`${i}-${type}-${j}`} sx={{ backgroundColor, mb: 1 }}>
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Box className="flex items-center gap-2 w-full" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                {getIcon(status)}
                                                                <Box className="flex items-center justify-between w-full">
                                                                    <Box className="flex items-center gap-2">
                                                                        <span>{param.name}</span>
                                                                        <Typography variant="body2">({param.value})</Typography>
                                                                    </Box>
                                                                    {getStatusTag(status)}
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Typography variant="body2" sx={{ color: "#333" }}>
                                                                <strong>Reference Range:</strong> {param.reference_range}
                                                            </Typography>
                                                            {param.reason && (
                                                                <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
                                                                    <strong>Note:</strong> {param.reason}
                                                                </Typography>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })
                                        )
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                </div>
            </div>
        </div>
    );


}

export default ViewReportList